import type { InviteRole } from "../services/naraDoor";

const TOKEN_KEY = "narahub-invite-token";

// Kept across the sign-in round trip so the link still works if the URL is lost.
export const storeInviteToken = (token: string) => sessionStorage.setItem(TOKEN_KEY, token);
export const readInviteToken = () => sessionStorage.getItem(TOKEN_KEY);
export const clearInviteToken = () => sessionStorage.removeItem(TOKEN_KEY);

/** Callable error code without the SDK's "functions/" prefix; "" when the error is not a callable one. */
export function callableErrorCode(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code.replace(/^functions\//, "") : "";
}

const REDEEM_ERROR_KEYS: Record<string, string> = {
  "permission-denied": "invite.errors.forbidden",
  "not-found": "invite.errors.notFound",
  "invalid-argument": "invite.errors.missing",
};

/** The server explains a refused link (used, expired); every other failure gets our own copy. */
export function redeemErrorMessage(err: unknown, t: (key: string) => string): string {
  const code = callableErrorCode(err);
  if (code === "failed-precondition" && err instanceof Error && err.message) return err.message;
  return t(REDEEM_ERROR_KEYS[code] ?? "invite.errors.generic");
}

/** A project invite is scoped to its project; a workspace invite only carries projects for a field agent. */
export const inviteProjectIds = (role: string, projectId: string | undefined, picked: string[]) =>
  projectId ? [projectId] : role === "uploader" ? picked : undefined;

/** An uploader seat is useless without a project, and the server refuses it. */
export const uploaderNeedsProject = (role: string, projectIds?: string[]) =>
  role === "uploader" && !projectIds?.length;

const REINVITE_ROLES = ["admin", "editor", "viewer"] as const;

/**
 * Role and projects for re-inviting an existing seat. A field agent (uploader) keeps
 * its projects; without any the server refuses the invite, so null. An unknown role falls to the
 * least privilege, never up.
 */
export function reinviteTarget(
  workspaceRole: string | null | undefined,
  projectIds: string[],
): { role: InviteRole; projectIds?: string[] } | null {
  if (workspaceRole === "uploader") {
    return projectIds.length ? { role: "uploader", projectIds } : null;
  }
  const known = REINVITE_ROLES.find((r) => r === workspaceRole);
  return { role: known ?? "viewer" };
}
